// @ts-nocheck
/**
 * "Working Capital" tab body for the unified mega-card — the cross-statement
 * CCC view over a unified parent session.
 *
 * Lazy-fetches GET /agent/sessions/{parentId}/working-capital (deterministic,
 * computed server-side from the two children's mappings; see src/wc_ccc) and
 * renders: a scrubbable ledger of day counts + NWC (inline sparkline per
 * metric, one shared hovered-period state resting on the FIRST column), the
 * cycle-components line chart (days), the working-capital line chart ($),
 * cross-statement integrity chips, and the construction
 * retainage detail when present. Exporting (the CCC workbook among the other
 * parts) lives in the unified card's header download popover.
 *
 * Chart idiom follows RatiosTab exactly (recharts, shared hover, index-based
 * x-axis thinning — period labels stay opaque ordinal tokens). Series colors
 * are the app palette in a CVD-validated order: DSO blue, DIO amber, DPO
 * violet, CCC green; identity is never color-alone (legend + tooltip + the
 * ledger's numeric readout).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Loader2,
  Recycle,
  TrendingUp,
  XCircle,
} from 'lucide-react';

import { getWorkingCapital } from '../../utils/agentApi';
import './RatiosTab.css';
import './WorkingCapitalTab.css';

const fmtDays = (v) =>
  v == null || Number.isNaN(v) ? '—' : `${v.toFixed(1)}d`;
const fmtPct = (v) =>
  v == null || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(1)}%`;
const fmtMoney = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};
const moneyTick = (v) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return `${v}`;
};

// Fixed series identity (never re-assigned when a series is missing):
// validated adjacent-pair CVD order blue → amber → violet → green.
const CYCLE_SERIES = [
  { key: 'dso_ending', name: 'DSO', color: '#3b82f6' },
  { key: 'dio_ending', name: 'DIO', color: '#f59e0b' },
  { key: 'dpo_ending', name: 'DPO', color: '#8b5cf6' },
  { key: 'ccc_ending', name: 'CCC', color: '#10b981' },
];
const NWC_SERIES = [
  { key: 'nwc', name: 'Net working capital', color: '#3b82f6' },
  { key: 'nwc_ex_cash', name: 'NWC ex cash', color: '#f59e0b' },
  { key: 'trade_working_capital', name: 'Trade WC', color: '#10b981' },
];

const X_AXIS_TICK_BUDGET = 24;

const SPARK_W = 140;
const SPARK_H = 26;
const SPARK_PAD = 3;

const Sparkline = ({ values, color, activeIdx }) => {
  const n = values.length;
  const finite = values.filter((v) => v != null && !Number.isNaN(v));
  const xAt = (i) =>
    n > 1 ? SPARK_PAD + (i * (SPARK_W - 2 * SPARK_PAD)) / (n - 1) : SPARK_W / 2;
  let yAt = () => SPARK_H / 2;
  if (finite.length) {
    const min = Math.min(...finite);
    const max = Math.max(...finite);
    const span = max - min;
    yAt = (v) =>
      span === 0
        ? SPARK_H / 2
        : SPARK_H - SPARK_PAD - ((v - min) / span) * (SPARK_H - 2 * SPARK_PAD);
  }
  const segments = [];
  let cur = [];
  values.forEach((v, i) => {
    if (v == null || Number.isNaN(v)) {
      if (cur.length) segments.push(cur);
      cur = [];
    } else {
      cur.push({ x: xAt(i), y: yAt(v) });
    }
  });
  if (cur.length) segments.push(cur);
  const active = values[activeIdx];
  return (
    <svg
      className="rt-spark"
      width={SPARK_W}
      height={SPARK_H}
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      aria-hidden="true"
    >
      {finite.length === 0 && (
        <line
          x1={SPARK_PAD}
          x2={SPARK_W - SPARK_PAD}
          y1={SPARK_H / 2}
          y2={SPARK_H / 2}
          stroke={color}
          strokeDasharray="2 3"
          opacity={0.3}
        />
      )}
      {segments.map((pts, si) =>
        pts.length > 1 ? (
          <polyline
            key={si}
            points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.8}
          />
        ) : (
          <circle
            key={si}
            cx={pts[0].x}
            cy={pts[0].y}
            r={1.5}
            fill={color}
            opacity={0.8}
          />
        ),
      )}
      {active != null && !Number.isNaN(active) && (
        <circle cx={xAt(activeIdx)} cy={yAt(active)} r={2.5} fill={color} />
      )}
    </svg>
  );
};

const TOOLTIP_STYLE = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text)',
};

const CHECK_META = {
  balanced: { tone: 'good', icon: CheckCircle },
  // The mapped gap reproduces the source workbook's own imbalance: the
  // mapping reconciles to its source, the source just does not balance.
  source_unbalanced: { tone: 'good', icon: CheckCircle, text: 'matches source' },
  tied: { tone: 'good', icon: CheckCircle },
  mismatch: { tone: 'bad', icon: XCircle },
  not_found: { tone: 'muted', icon: HelpCircle },
  unknown: { tone: 'muted', icon: HelpCircle },
};

const CheckChip = ({ label, check }) => {
  const meta = CHECK_META[check?.status] || CHECK_META.unknown;
  const Icon = meta.icon;
  return (
    <span
      className={`wct-chip wct-chip-${meta.tone}`}
      title={check?.note || `${label}: ${check?.status || 'unknown'}`}
    >
      <Icon size={11} />
      <span>
        {label}: {meta.text || (check?.status || 'unknown').replace('_', ' ')}
      </span>
    </span>
  );
};

const WorkingCapitalTab = ({ sessionId }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, error: null });
    setHoverIdx(null);
    getWorkingCapital(sessionId)
      .then((d) => {
        if (!cancelled) setState({ status: 'ready', data: d, error: null });
      })
      .catch((err) => {
        if (!cancelled)
          setState({ status: 'error', data: null, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const r = state.data;
  const labels = useMemo(() => (r?.columns || []).map((c) => c.label), [r]);
  const chartRows = useMemo(() => {
    if (!r) return [];
    return labels.map((label, i) => {
      const row = { period: label };
      for (const s of [...CYCLE_SERIES, ...NWC_SERIES]) {
        row[s.key] = r.series?.[s.key]?.[i] ?? null;
      }
      return row;
    });
  }, [r, labels]);

  if (state.status === 'loading') {
    return (
      <div className="rt-state">
        <Loader2 size={16} className="rt-spin" />
        <span>Computing working capital…</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="rt-state rt-state-error">
        <AlertTriangle size={16} />
        <span>{state.error || 'Could not load working capital.'}</span>
      </div>
    );
  }
  if (!r || r.insufficient_data || !r.columns || r.columns.length === 0) {
    return (
      <div className="rt-wrap">
        <div className="rt-state">
          <AlertTriangle size={16} />
          <span>
            The P&amp;L periods and balance-sheet snapshots could not be joined
            {r?.join_status ? ` (${r.join_status})` : ''}. Working-capital
            metrics need matching period labels across both statements.
          </span>
        </div>
        {(r?.notes || []).length > 0 && (
          <div className="rt-footnotes">
            {r.notes.map((n, i) => (
              <div key={i}>· {n}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const n = r.columns.length;
  const xTickInterval =
    n > X_AXIS_TICK_BUDGET ? Math.ceil(n / X_AXIS_TICK_BUDGET) - 1 : 0;
  // Resting position is the FIRST column — the baseline, not the latest.
  const activeIdx = Math.min(hoverIdx != null ? hoverIdx : 0, n - 1);
  const activeLabel = labels[activeIdx];
  const clearHover = () => setHoverIdx(null);

  const scrubFromRow = (e) => {
    const svg = e.currentTarget.querySelector('.rt-spark');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return;
    if (e.clientX < rect.left - 12 || e.clientX > rect.right + 12) return;
    const frac =
      (e.clientX - rect.left - SPARK_PAD) /
      Math.max(1, rect.width - 2 * SPARK_PAD);
    setHoverIdx(Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1)))));
  };

  const syncFromChart = (st) => {
    if (!st) return;
    const byIdx = Number(st.activeTooltipIndex);
    if (Number.isInteger(byIdx) && byIdx >= 0 && byIdx < n) {
      setHoverIdx(byIdx);
      return;
    }
    if (st.activeLabel != null) {
      const j = labels.indexOf(st.activeLabel);
      if (j >= 0) setHoverIdx(j);
    }
  };

  const series = r.series || {};
  const cons = r.construction?.series || null;

  const ledger = [
    {
      key: 'dso_ending',
      label: 'DSO',
      fmt: fmtDays,
      spark: '#3b82f6',
      tip: 'Days sales outstanding: receivables over the trailing year of revenue, times 365. Columns before a full year of history use the expanding period-to-date window with a matched day count. The diligence headline; how long a sale waits to become cash.',
    },
    {
      key: 'dio_ending',
      label: 'DIO',
      fmt: fmtDays,
      spark: '#f59e0b',
      tip: 'Days inventory outstanding: inventory over the trailing year of cost of sales, times 365 (expanding window with a matched day count before a full year). How long stock sits before it ships.',
    },
    {
      key: 'dpo_ending',
      label: 'DPO',
      fmt: fmtDays,
      spark: '#8b5cf6',
      tip: 'Days payable outstanding: payables over the trailing year of cost of sales, times 365 (expanding window with a matched day count before a full year). How long suppliers wait to be paid.',
    },
    {
      key: 'ccc_ending',
      label: 'CCC',
      fmt: fmtDays,
      spark: '#10b981',
      tip: 'Cash conversion cycle: DSO + DIO - DPO. How many days a dollar stays locked inside the operating cycle.',
    },
    {
      key: 'nwc_ex_cash',
      label: 'NWC ex cash',
      fmt: fmtMoney,
      spark: '#f59e0b',
      tip: 'Current assets excluding cash, minus current liabilities. The working capital the business itself absorbs.',
    },
    {
      key: 'nwc_ex_cash_pct_revenue',
      label: 'NWC intensity',
      fmt: fmtPct,
      spark: '#94a3b8',
      tip: 'NWC ex cash as a share of annual revenue (partial windows scale to the annual-equivalent). Rising intensity means each revenue dollar locks up more cash.',
    },
    {
      key: 'nwc_cash_delta',
      label: 'Cash released / (absorbed)',
      fmt: fmtMoney,
      spark: '#94a3b8',
      tip: 'The change in NWC ex cash, sign-flipped: positive means working capital released cash over the comparison window.',
    },
  ];

  return (
    <div className="rt-wrap">
      <div className="wct-toprow">
        <div className="wct-chips">
          <span className="wct-chip wct-chip-muted" title="How the P&L windows were paired with balance-sheet snapshots">
            <Recycle size={11} />
            <span>join: {r.join_status}</span>
          </span>
          <CheckChip label="A = L + E" check={r.checks?.balance} />
          <CheckChip label="Earnings tie" check={r.checks?.cye_ni_tie} />
        </div>
      </div>

      <div className="rt-ledger-block">
        <div className="rt-period-pill">{activeLabel}</div>
        <div className="rt-ledger" onMouseLeave={clearHover}>
          {ledger.map((t) => {
            const vals = series[t.key] || [];
            const raw = vals[activeIdx];
            return (
              <div
                className="rt-ledger-row"
                key={t.label}
                onMouseMove={scrubFromRow}
              >
                <span className="rt-ledger-label">
                  {t.label}
                  <span
                    className="rt-help"
                    data-tip={t.tip}
                    role="img"
                    aria-label={`${t.label}: ${t.tip}`}
                    tabIndex={0}
                  >
                    <HelpCircle size={12} />
                  </span>
                </span>
                <Sparkline values={vals} color={t.spark} activeIdx={activeIdx} />
                <span className="rt-ledger-value">{t.fmt(raw)}</span>
              </div>
            );
          })}
        </div>
        {r.headline?.label && (
          <div className="wct-headline">
            Latest readable: {r.headline.label} ({r.headline.basis} basis)
            {r.headline.ccc != null && <> · CCC {fmtDays(r.headline.ccc)}</>}
            {r.headline.nwc_ex_cash != null && (
              <> · NWC ex cash {fmtMoney(r.headline.nwc_ex_cash)}</>
            )}
            {r.headline.revenue_per_day != null && (
              <> · 1 day of DSO = {fmtMoney(r.headline.revenue_per_day)}</>
            )}
          </div>
        )}
      </div>

      <div className="rt-chart-card">
        <h4 className="rt-chart-title">
          <TrendingUp size={13} /> Cash conversion cycle (days, ending basis)
        </h4>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={chartRows}
            margin={{ top: 8, right: 16, left: 0, bottom: 40 }}
            onMouseMove={syncFromChart}
            onMouseLeave={clearHover}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10, fill: 'var(--text)' }}
              interval={xTickInterval}
              angle={-30}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--text)' }}
              tickFormatter={(v) => `${v}d`}
              width={48}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => fmtDays(v)}
              cursor={false}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {hoverIdx != null && (
              <ReferenceLine x={activeLabel} stroke="#94a3b8" strokeDasharray="3 3" />
            )}
            {CYCLE_SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="rt-chart-card">
        <h4 className="rt-chart-title">
          <TrendingUp size={13} /> Working capital position
        </h4>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={chartRows}
            margin={{ top: 8, right: 16, left: 0, bottom: 40 }}
            onMouseMove={syncFromChart}
            onMouseLeave={clearHover}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10, fill: 'var(--text)' }}
              interval={xTickInterval}
              angle={-30}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--text)' }}
              tickFormatter={moneyTick}
              width={56}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => fmtMoney(v)}
              cursor={false}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {hoverIdx != null && (
              <ReferenceLine x={activeLabel} stroke="#94a3b8" strokeDasharray="3 3" />
            )}
            {NWC_SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {cons && (
        <div className="rt-chart-card">
          <h4 className="rt-chart-title">Construction detail (retainage)</h4>
          <div className="rt-ledger" onMouseLeave={clearHover}>
            {[
              { key: 'retention_receivable', label: 'Retention receivable', fmt: fmtMoney, spark: '#3b82f6' },
              { key: 'retention_pct_ar', label: 'Retention, % of AR', fmt: fmtPct, spark: '#94a3b8' },
              { key: 'dso_ex_retention', label: 'DSO ex retention', fmt: fmtDays, spark: '#3b82f6' },
              { key: 'retainage_payable', label: 'Retainage payable', fmt: fmtMoney, spark: '#8b5cf6' },
              { key: 'dpo_incl_retainage', label: 'DPO incl retainage', fmt: fmtDays, spark: '#8b5cf6' },
              { key: 'net_underbillings', label: 'Net underbillings / (overbillings)', fmt: fmtMoney, spark: '#f59e0b' },
            ]
              .filter((t) => (cons[t.key] || []).some((v) => v != null))
              .map((t) => {
                const vals = cons[t.key] || [];
                return (
                  <div
                    className="rt-ledger-row"
                    key={t.key}
                    onMouseMove={scrubFromRow}
                  >
                    <span className="rt-ledger-label">{t.label}</span>
                    <Sparkline values={vals} color={t.spark} activeIdx={activeIdx} />
                    <span className="rt-ledger-value">{t.fmt(vals[activeIdx])}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingCapitalTab;
